import { Box, Drawer, List } from '@mui/material'
import { styled } from '@mui/system'
import HomeIcon from '@mui/icons-material/Home'
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox'
import SettingsIcon from '@mui/icons-material/Settings'
import DonutSmallIcon from '@mui/icons-material/DonutSmall'
import EmailIcon from '@mui/icons-material/Email'
import IncomeApprovalIcon from '@mui/icons-material/FactCheck'
import { NavBarItem } from './NavBarLink'
import * as AppRoutes from '../../Pages/Routes'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import TaskIcon from '@mui/icons-material/Task'
import mieccLogo from '../../Assets/Logos/MiECC.png'
import GroupWorkIcon from '@mui/icons-material/GroupWork'
import { usePermissions } from '../../hooks/use-permissions'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import CareManagementIcon from '@mui/icons-material/AssignmentInd'
import { useAuth } from '../../hooks/use-auth'
import { useEffect, useState } from 'react'
import { generatePath } from 'react-router'
import { AssignmentLate } from '@mui/icons-material'
import { Role } from '../../core/types'
import { FindChildrenIcon } from '../../Assets/SVGs/FindChildren'

export const NavBar = styled(internalNavBar)(({ theme }) => ({
}))

interface Props {
  current: string
  onClick: (selection: string) => void
  visible: boolean
  setVisible: (showBurgerMenu: boolean) => void
}

function internalNavBar (props: Props): JSX.Element {
  function clickDrawer (selection: string): void {
    props.onClick(selection)
  }

  const perms = usePermissions()
  const { user } = useAuth()
  const theme = useTheme()
  const largeWindow = useMediaQuery(theme.breakpoints.up('md'))
  const [caregiverPortalRoute, setCaregiverPortalRoute] = useState<string>('')
  const [caregiverEnrollmentsRoute, setCaregiverEnrollmentsRoute] = useState<string>('')
  const [isInCaregiverPortal, setIsInCaregiverPortal] = useState<boolean>(false)
  const canAccessCaregiverPortal = user == null || perms.CaregiverPortal

  useEffect(() => {
    const isInPortal = props.current.toLowerCase().includes('caregiverportal')
    setIsInCaregiverPortal(isInPortal)

    if (isInPortal) {
      // Need to pass along the isdId to the CaregiverPortal routes.
      // Can't use useParams here because it's not a route component.
      const startIndex = location.pathname.toLowerCase().indexOf('caregiverportal/') + 16

      const substr = location.pathname.substring(startIndex)
      const params = substr.split('/')

      let isdId = ''
      let isdServiceProviderId = ''

      if (params.length > 0) {
        isdId = params[0]
        if (params.length > 2) {
          isdServiceProviderId = params[2]
        }
      }

      if (!isNaN(parseInt(isdId))) {
        setCaregiverPortalRoute(generatePath(AppRoutes.CAREGIVER_PORTAL, { isdId: isdId?.toString() ?? '', isdServiceProviderId: isdServiceProviderId?.toString() ?? '' }))
        setCaregiverEnrollmentsRoute(generatePath(AppRoutes.CAREGIVER_ENROLLMENTS, { isdId: isdId?.toString() ?? '', isdServiceProviderId: isdServiceProviderId?.toString() ?? '' }))
      }
    }
  }, [location])

  const caregiverItems = <>
    {(isInCaregiverPortal && (user == null || perms.CaregiverPortal_Enrollments) && caregiverEnrollmentsRoute !== '') &&
      <NavBarItem current={props.current} value={caregiverEnrollmentsRoute} label='Enrollments' onClick={clickDrawer} icon={<CalendarMonthIcon />} />
    }
    {(isInCaregiverPortal && perms.CaregiverPortal) &&
      <>
        <NavBarItem current={props.current} value={AppRoutes.CAREGIVER_PORTAL_MEMBERS} label='Members' onClick={clickDrawer} icon={<PeopleAltIcon />} />
        <NavBarItem current={props.current} value={AppRoutes.CAREGIVER_PORTAL_SERVICE_FINDER} label='Service Finder' onClick={clickDrawer} icon={<CalendarMonthIcon />} />
      </>
    }
  </>

  const careNetworkItems = <>
    <NavBarItem current={props.current} value={AppRoutes.FIND_CHILDREN} label='Find Children' onClick={clickDrawer} icon={<FindChildrenIcon />} />
  </>
  const enrollmentItems = <>
    <NavBarItem current={props.current} value={AppRoutes.ENROLLMENT_MANAGEMENT} label='Enrollments' onClick={clickDrawer} icon={<MoveToInboxIcon />}/>
    {perms.WaiverApprovals &&
      <NavBarItem current={props.current} value={AppRoutes.INCOME_APPROVALS} label='Income Approval' onClick={clickDrawer} icon={<IncomeApprovalIcon />}/>
    }
  </>

  const incidentItems = <>
    <NavBarItem current={props.current} value={AppRoutes.RESTRAINT_LIST} label='Restraint Incidents' onClick={clickDrawer} icon={<AssignmentLate />} />
  </>

  const accessRequestItems = <>
    <NavBarItem current={props.current} value={AppRoutes.ACCESS_REQUESTS} label='Access Requests' onClick={clickDrawer} icon={<TaskIcon />} />
  </>

  const reportsItems = <>
    <NavBarItem current={props.current} value={AppRoutes.REPORTS} label='Reports' onClick={clickDrawer} icon={<DonutSmallIcon />}/>
  </>

  const careManagementItems = <>
    <NavBarItem current={props.current} value={AppRoutes.CARE_MANAGEMENT} label='Care Management' onClick={clickDrawer} icon={<CareManagementIcon />} />
  </>
  const spAdminItems = <>
    <NavBarItem current={props.current} value={AppRoutes.SERVICE_PROVIDER_ADMIN_SERVICES} label='Services' onClick={clickDrawer} icon={<CalendarMonthIcon />} />
    <NavBarItem current={props.current} value={AppRoutes.SERVICE_PROVIDER_ADMIN_SETTINGS} label='Service Provider Settings' onClick={clickDrawer} icon={<SettingsIcon />} />
  </>

  const isdAdminItems = <>
    <NavBarItem current={props.current} value={AppRoutes.ISD_SETTINGS} label='ISD Settings' onClick={clickDrawer} icon={<SettingsIcon />} />
    <NavBarItem current={props.current} value={AppRoutes.SERVICE_PROVIDER_MANAGEMENT} label='Provider Management' onClick={clickDrawer} icon={<GroupWorkIcon />} />
    {perms.ISDs_Approvals && (<>
      <NavBarItem current={props.current} value={AppRoutes.ISD_INCOME_APPROVALS} label='Income Approval' onClick={clickDrawer} icon={<IncomeApprovalIcon />}/>
    </>
    )}
  </>
  const miEccAdminItems = <>
    <NavBarItem current={props.current} value={AppRoutes.ISD_MANAGEMENT} label='ISD Management' onClick={clickDrawer} icon={<GroupWorkIcon />} />
    <NavBarItem current={props.current} value={AppRoutes.APPLICATION_SETTINGS} label='Application Settings' onClick={clickDrawer} icon={<SettingsIcon />} />
  </>
  const caregiverOutreachItems = <NavBarItem current={props.current} value={AppRoutes.CAREGIVER_OUTREACH} label='Caregiver Outreach' onClick={clickDrawer} icon={<EmailIcon />} />

  let homeRoute = AppRoutes.LANDING_PATH
  if (canAccessCaregiverPortal) {
    if (user != null) {
      homeRoute = AppRoutes.CAREGIVER_PORTAL_DASHBOARD
    } else if (isInCaregiverPortal) {
      homeRoute = caregiverPortalRoute
    }
  }

  const drawerContent =
    <div>
      <img src={mieccLogo} height={40} alt='logo' data-testid="mieccLogo" style={{
        marginTop: '20px',
        marginLeft: '20px'
      }}
      />
      <List style={{ maxWidth: '260px' }}>
        <NavBarItem current={props.current} value={homeRoute} label='Home' onClick={clickDrawer} icon={<HomeIcon />}/>
        {(isInCaregiverPortal && canAccessCaregiverPortal) && (caregiverItems)}
        {perms.CareNetwork && (careNetworkItems)}
        {perms.Enrollment && (enrollmentItems)}
        {perms.ISDs_Approvals && (accessRequestItems)}
        {(user?.securityLevel === Role.ISD_USER || user?.securityLevel === Role.SERVICE_PROVIDER_USER) && !user?.inactive && (incidentItems)}
        {perms.Reports && (reportsItems)}
        {(perms.CareNetwork_CareManagement) && (careManagementItems)}
        {perms.Users && (
          <NavBarItem current={props.current} value={AppRoutes.USER_MANAGEMENT} label='User Management' onClick={clickDrawer} icon={<PeopleAltIcon />} />
        )}
        {perms.ServiceProviders && (spAdminItems)}
        {perms.ISDs && (isdAdminItems)}
        {perms.MiECCAdmin && miEccAdminItems}
        {(user?.securityLevel === Role.ISD_USER || user?.securityLevel === Role.SERVICE_PROVIDER_USER || user?.securityLevel === Role.INTAKE_USER) && !user?.inactive && (caregiverOutreachItems)}
      </List>
    </div>

  return (
    <Box>
      <Drawer
        PaperProps={{ sx: { border: 'none' } }}
        color="primary"
        variant={largeWindow ? 'permanent' : 'temporary' }
        open={largeWindow || props.visible}
        anchor="left"
        onClose={() => { props.setVisible(false) }}
      >
        {drawerContent}
      </Drawer>
    </Box>

  )
}
